/**
 * Cloverhound design tokens (derived from Linear's design language).
 *
 * These are the raw values behind `cloverhound-theme.css`. Import them directly
 * when you need tokens from TS (charts, inline style props, Storybook args,
 * etc.). CSS-first consumers should use the variables in cloverhound-theme.css;
 * don't duplicate values.
 */

export const cloverhoundFonts = {
  sans:
    '"Inter Variable", "Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", "Linear Thai", sans-serif',
  mono:
    '"Berkeley Mono", "JetBrains Mono", "SF Mono", "SFMono-Regular", Consolas, "Liberation Mono", Menlo, Courier, monospace',
} as const;

/** Linear's rem scale — same names as their `--font-size-*` tokens. */
export const cloverhoundFontSize = {
  micro: "0.6875rem",
  mini: "0.75rem",
  small: "0.8125rem",
  regular: "0.9375rem",
  large: "1.125rem",
  title3: "1.25rem",
  title2: "1.5rem",
  title1: "2.25rem",
} as const;

/** Note: Linear's "normal" weight is 450, not 400 — this is intentional. */
export const cloverhoundFontWeight = {
  light: 300,
  normal: 450,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const cloverhoundRadius = {
  xs: "4px",
  sm: "6px",
  md: "8px",
  lg: "10px",
  xl: "12px",
  "2xl": "16px",
  pill: "9999px",
} as const;

export const cloverhoundDuration = {
  quick: "100ms",
  regular: "250ms",
  slow: "350ms",
} as const;

export const cloverhoundEasing = {
  inQuad: "cubic-bezier(0.55, 0.085, 0.68, 0.53)",
  outQuad: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  inOutQuad: "cubic-bezier(0.455, 0.03, 0.515, 0.955)",
  inCubic: "cubic-bezier(0.55, 0.055, 0.675, 0.19)",
  outCubic: "cubic-bezier(0.215, 0.61, 0.355, 1)",
  inOutCubic: "cubic-bezier(0.645, 0.045, 0.355, 1)",
  inQuart: "cubic-bezier(0.895, 0.03, 0.685, 0.22)",
  outQuart: "cubic-bezier(0.165, 0.84, 0.44, 1)",
  inOutQuart: "cubic-bezier(0.77, 0, 0.175, 1)",
  inQuint: "cubic-bezier(0.755, 0.05, 0.855, 0.06)",
  outQuint: "cubic-bezier(0.23, 1, 0.32, 1)",
  inOutQuint: "cubic-bezier(0.86, 0, 0.07, 1)",
  inExpo: "cubic-bezier(0.95, 0.05, 0.795, 0.035)",
  outExpo: "cubic-bezier(0.19, 1, 0.22, 1)",
  inOutExpo: "cubic-bezier(1, 0, 0, 1)",
  inCirc: "cubic-bezier(0.6, 0.04, 0.98, 0.335)",
  outCirc: "cubic-bezier(0.075, 0.82, 0.165, 1)",
  inOutCirc: "cubic-bezier(0.785, 0.135, 0.15, 0.86)",
} as const;

/**
 * Linear's chromatic palette — used for status, priority, labels, avatars.
 * The same hex values are used in both light and dark (Linear does not
 * re-tint these per-theme).
 */
export const cloverhoundPalette = {
  gray: {
    50: "#fcfcfd",
    100: "#f6f6f7",
    150: "#f0f0f0",
    200: "#ebeced",
    250: "#e0e0e0",
    300: "#d5d5d5",
    400: "#b0b5c0",
    500: "#9f9fa1",
    600: "#6b6f76",
    700: "#5c5d5f",
    800: "#23252a",
    850: "#1c1e21",
    900: "#0f0f11",
    950: "#090909",
  },
  indigo: {
    50: "#ebecf5",
    100: "#d7dbf0",
    200: "#b4bae6",
    300: "#8a94dd",
    400: "#6d78d5",
    500: "#5e69c1",
    600: "#4f5ec4",
    700: "#4656bb",
    800: "#3a4899",
    900: "#2c366f",
  },
  blue: {
    50: "#e6f6ff",
    100: "#bfe7ff",
    200: "#95d9ff",
    300: "#55ccff",
    400: "#3dbfff",
    500: "#2bb7ff",
    600: "#007eff",
    700: "#0068d6",
  },
  cyan: {
    300: "#3ccfe0",
    500: "#00a2b5",
    700: "#0092b9",
  },
  green: {
    50: "#e6f6ea",
    100: "#c7ebd0",
    300: "#43bc58",
    500: "#32ae4b",
    700: "#007a1a",
  },
  yellow: {
    50: "#fffbe6",
    100: "#ffeac6",
    400: "#fad700",
    500: "#e0b100",
    600: "#d7a900",
  },
  orange: {
    50: "#ffe7de",
    300: "#e85c20",
    500: "#cf4608",
    700: "#bb3400",
  },
  red: {
    50: "#f4eaec",
    500: "#c61c32",
    700: "#9a142a",
  },
} as const;

/** Linear's semantic status/priority color mapping. */
export const cloverhoundStatusColor = {
  backlog: cloverhoundPalette.gray[400],
  todo: cloverhoundPalette.gray[500],
  inProgress: cloverhoundPalette.yellow[500],
  inReview: cloverhoundPalette.indigo[400],
  done: cloverhoundPalette.indigo[600],
  completed: cloverhoundPalette.green[500],
  cancelled: cloverhoundPalette.gray[500],
  duplicate: cloverhoundPalette.gray[500],
} as const;

export const cloverhoundPriorityColor = {
  noPriority: cloverhoundPalette.gray[500],
  urgent: cloverhoundPalette.red[500],
  high: cloverhoundPalette.orange[300],
  medium: cloverhoundPalette.yellow[500],
  low: cloverhoundPalette.gray[400],
} as const;

export type LinearStatus = keyof typeof cloverhoundStatusColor;
export type LinearPriority = keyof typeof cloverhoundPriorityColor;
